// src/modules/app-views/invoices/pages/Document/tabs/PreviewTab/TotalsSection.jsx
// *******************************************************************************
// TOTALS SECTION - DANFE NFC-e
// *******************************************************************************
// Displays invoice totals and payment methods.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, Divider } from 'antd';
import financialService from 'services/financialService';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TotalsSection = ({ data }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const totals = data?.totals || {};
  const payment = data?.payment || {};
  const paymentMethods = payment.methods || [];

  // Extract totals
  const productsTotal = totals.productsAmount || totals.productsTotal || 0;
  const discount = totals.discountAmount || totals.discount || 0;
  const freight = totals.freightAmount || totals.freight || 0;
  const otherExpenses = totals.otherExpensesAmount || totals.otherExpenses || 0;
  const invoiceTotal = totals.documentAmount || totals.invoiceTotal || 0;
  const approximateTaxes = totals.approximateTaxes || 0;

  // Calculate payments total
  const paymentsTotal = paymentMethods.reduce((sum, p) => sum + (p.amount || 0), 0);
  const change = paymentsTotal > invoiceTotal ? paymentsTotal - invoiceTotal : 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="danfe-section danfe-totals">
      {/* Totals */}
      <div className="danfe-totals-block">
        {/* Subtotal (products) */}
        <div className="danfe-totals-row">
          <Text style={{ fontSize: 11 }}>Subtotal:</Text>
          <Text style={{ fontSize: 11 }}>{financialService.formatCurrency(productsTotal)}</Text>
        </div>

        {/* Discount (if any) */}
        {discount > 0 && (
          <div className="danfe-totals-row">
            <Text style={{ fontSize: 11 }}>Desconto:</Text>
            <Text style={{ fontSize: 11, color: '#52c41a' }}>
              - {financialService.formatCurrency(discount)}
            </Text>
          </div>
        )}

        {/* Freight (if any) */}
        {freight > 0 && (
          <div className="danfe-totals-row">
            <Text style={{ fontSize: 11 }}>Frete:</Text>
            <Text style={{ fontSize: 11 }}>{financialService.formatCurrency(freight)}</Text>
          </div>
        )}

        {/* Other expenses (if any) */}
        {otherExpenses > 0 && (
          <div className="danfe-totals-row">
            <Text style={{ fontSize: 11 }}>Outras Despesas:</Text>
            <Text style={{ fontSize: 11 }}>{financialService.formatCurrency(otherExpenses)}</Text>
          </div>
        )}

        {/* Invoice Total */}
        <div className="danfe-totals-row danfe-totals-total">
          <Text strong style={{ fontSize: 13 }}>
            TOTAL:
          </Text>
          <Text strong style={{ fontSize: 13 }}>
            {financialService.formatCurrency(invoiceTotal)}
          </Text>
        </div>

        {/* Approximate Taxes (Lei 12.741/2012) */}
        {approximateTaxes > 0 && (
          <div className="danfe-taxes-info" style={{ marginTop: 8, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 9 }}>
              Trib. Tot. Incid. (Lei Federal 12.741/12): {financialService.formatCurrency(approximateTaxes)}
            </Text>
          </div>
        )}
      </div>

      <Divider dashed style={{ margin: '8px 0' }} />

      {/* Payment Methods */}
      <div className="danfe-payments-block">
        <Text strong style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
          FORMA DE PAGAMENTO
        </Text>

        {paymentMethods.length > 0 ? (
          <>
            {paymentMethods.map((method, index) => (
              <div key={index} className="danfe-totals-row">
                <Text style={{ fontSize: 11 }}>{getPaymentMethodLabel(method)}</Text>
                <Text style={{ fontSize: 11 }}>{financialService.formatCurrency(method.amount || 0)}</Text>
              </div>
            ))}

            {/* Change (if any) */}
            {change > 0 && (
              <div className="danfe-totals-row">
                <Text style={{ fontSize: 11 }}>Troco:</Text>
                <Text style={{ fontSize: 11 }}>{financialService.formatCurrency(change)}</Text>
              </div>
            )}
          </>
        ) : (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Nenhuma forma de pagamento informada.
          </Text>
        )}
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TotalsSection;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Get payment method label for display
 * @param {Object} method - Payment method object
 * @returns {string} Payment method label
 */
function getPaymentMethodLabel(method) {
  // Payment type labels (NF-e standard)
  const typeLabels = {
    MONEY: 'Dinheiro',
    CASH: 'Dinheiro',
    CHECK: 'Cheque',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
    STORE_CREDIT: 'Crédito Loja',
    FOOD_VOUCHER: 'Vale Alimentação',
    MEAL_VOUCHER: 'Vale Refeição',
    GIFT_CARD: 'Vale Presente',
    FUEL_VOUCHER: 'Vale Combustível',
    TRADE_BILL: 'Duplicata Mercantil',
    BANK_SLIP: 'Boleto Bancário',
    BANK_DEPOSIT: 'Depósito Bancário',
    PIX: 'PIX',
    TRANSFER: 'Transferência',
    CASHBACK: 'Cashback',
    NO_PAYMENT: 'Sem Pagamento',
    OTHER: 'Outros',
  };

  // Try type label first
  if (method.type && typeLabels[method.type]) {
    return typeLabels[method.type];
  }

  // Fallback to code-based labels (NF-e codes)
  const codeLabels = {
    '01': 'Dinheiro',
    '02': 'Cheque',
    '03': 'Cartão de Crédito',
    '04': 'Cartão de Débito',
    '05': 'Crédito Loja',
    '10': 'Vale Alimentação',
    '11': 'Vale Refeição',
    '12': 'Vale Presente',
    '13': 'Vale Combustível',
    '14': 'Duplicata Mercantil',
    '15': 'Boleto Bancário',
    '16': 'Depósito Bancário',
    '17': 'PIX',
    '18': 'Transferência',
    '19': 'Cashback',
    '90': 'Sem Pagamento',
    '99': 'Outros',
  };

  if (method.code && codeLabels[method.code]) {
    return codeLabels[method.code];
  }

  // Last resort: try translateX or use description
  const translated = translateX(`payment_method_${method.type}`);
  if (translated && !translated.startsWith('payment_method_')) {
    return translated;
  }

  return method.description || method.type || 'Outros';
}
